import { getPropertyName } from 'utils/Studio/getPropertyName/getPropertyName';
import { ElementTypes } from '@components/Studio/QueryCreationTwo/ActionButtons/utils';

import {
  IRuntimeDependencies,
  CalcCounts,
  SortCounts,
  CalcCountsObj,
} from '../constants/runtime.interfaces';
import { filterDepsType } from '@redux/Studio/QueryCreation/utils/getFilterPropDeps';
import {
  TCalcArg,
  ICalcs,
  IQuestionDefinition,
  IRuntimeSort,
  PropertyFilterItem,
  TAllWindowFunctionDeps,
  TPropertyDepWindowFunctions,
} from '@redux/Studio/QueryCreation/constants/queryCreation.interface';
import { IOutputItem } from 'Modules/Studio/Questions/EditQuestion/SubQuestions/redux/constants/subQuestions.interface';
import { EMPTY_CALC_COUNTS, EMPTY_SORT_COUNTS } from '../constants/runtime.constants';
import { getWFDeps } from './getWFDeps';

export interface IDependencyCalc {
  aggrTypes: string[];
  id: number;
  name: string;
  propName: string;
}

export interface IPropertyFilterDependency {
  conceptId: string;
  conceptLabel: string;
  propertyUri: string;
  propertyLabel: string;
  prop: IOutputItem;
}
export interface IDependencyErrors {
  calcs: CalcCountsObj[];
  isSorted: boolean;
  isUsedInQuestionWideFilter: boolean;
  usedInPropertyFilters: IPropertyFilterDependency[];
  WFDeps: TAllWindowFunctionDeps;
}

export const depErrorsInitialState: IDependencyErrors = {
  calcs: [],
  isSorted: false,
  isUsedInQuestionWideFilter: false,
  usedInPropertyFilters: [],
  WFDeps: { properties: [], calcs: [] },
};

export const runtimeDependenciesInitialState: IRuntimeDependencies = {
  sortCounts: Object.assign({}, EMPTY_SORT_COUNTS),
  calcCounts: Object.assign({}, EMPTY_CALC_COUNTS),
  isSorted: false,
  hasCalcs: false,
  isUsedInQuestionWideFilter: false,
  usedInPropertyFilters: [],
  WFDeps: { properties: [], calcs: [] },
};

/**
 * Generic helper to count how many times a property or calc is used in runtime sort
 * @param setting
 * @param isMatch
 * @param prop
 * @param aggr
 * @param calc
 * @param aggrTypes
 * @returns SortCounts
 */
const getSortCounts = (
  setting: IRuntimeSort,
  isMatch: boolean,
  prop: number,
  aggr: number,
  calc: number,
  aggrTypes: string[]
): SortCounts => {
  const isProp = setting.elementType === ElementTypes.isProp;
  const isAggr = setting.elementType === ElementTypes.isAggr;
  const isCalc = setting.elementType === ElementTypes.isCalc;

  let aggrTypesUpdate = aggrTypes;

  if (isMatch) {
    if (isProp) {
      prop += 1;
    } else if (isAggr) {
      aggr += 1;
      aggrTypesUpdate = [...aggrTypes, setting?.elementProps?.aggrType || ''];
    } else if (isCalc) {
      calc += 1;
    }
  }

  return {
    prop,
    aggr,
    calc,
    aggrTypes: aggrTypesUpdate,
  } as SortCounts;
};

/**
 *
 * @param sortSettings
 * @param acc
 * @param matcherFn
 * @returns
 */
const reduceSortSettings = (
  sortSettings: IRuntimeSort[],
  acc: SortCounts,
  matcherFn: (args: { setting: IRuntimeSort }) => boolean
): SortCounts => {
  return sortSettings.reduce((sortAcc: SortCounts, setting) => {
    const isMatch = matcherFn({ setting });

    const counts = getSortCounts(
      setting,
      isMatch,
      sortAcc.prop,
      sortAcc.aggr,
      sortAcc.calc,
      sortAcc.aggrTypes
    );

    return {
      ...sortAcc,
      ...counts,
    };
  }, acc);
};

/**
 * reduces properties and iterates sortSettings to get counts of prop or aggregate used in sort settings
 * references in the runtimeSettings.sort
 * @param sortSettings
 * @param properties
 * @returns SortCounts
 */
const getSortCountsForProps = (
  sortSettings: IRuntimeSort[],
  properties: IOutputItem[]
): SortCounts => {
  const results = properties.reduce(
    (acc: SortCounts, property) => {
      const matcherFn = (args: { setting: IRuntimeSort }) =>
        args.setting?.elementProps?.concept === property.conceptId &&
        args.setting?.elementProps?.property === property.uri;

      return {
        ...acc,
        ...reduceSortSettings(sortSettings, acc, matcherFn),
      };
    },
    Object.assign({}, EMPTY_SORT_COUNTS)
  );

  return results;
};

/**
 * reduces calcs and iterates sortSettings to get counts of calc's used in sorting
 * references in the runtimeSettings.sort
 * @param sortSettings
 * @param calcs
 * @returns SortCounts
 */
const getSortCountsForCalcs = (sortSettings: IRuntimeSort[], calcs: ICalcs[]): SortCounts => {
  const results = calcs.reduce(
    (acc: SortCounts, calc) => {
      const matcherFn = (args: { setting: IRuntimeSort }) =>
        args.setting?.elementProps?.calcId === calc.id;

      return {
        ...acc,
        ...reduceSortSettings(sortSettings, acc, matcherFn),
      };
    },
    Object.assign({}, EMPTY_SORT_COUNTS)
  );

  return results;
};

/**
 * reduce calc arguments to look for calc, prop or aggr dependency counts
 * @param calcArgs
 * @param matcherFn
 * @returns
 */
const reduceCalcArgs = (
  calcArgs: TCalcArg[],
  matcherFn: (arg: TCalcArg) => boolean
): SortCounts => {
  return calcArgs.reduce(
    (acc: SortCounts, arg) => {
      const { concept, property, calcId, aggrType } = arg;
      const isCalc = aggrType == null && concept === null && property === null && calcId !== null;
      const isProp = aggrType === null && concept !== null && property !== null && calcId === null;
      const isAggr = aggrType !== null;

      const isMatch = matcherFn(arg);

      if (isMatch) {
        return {
          ...acc,
          prop: isProp ? acc.prop + 1 : acc.prop,
          aggr: isAggr && aggrType ? acc.aggr + 1 : acc.aggr,
          aggrTypes: isAggr && aggrType ? [...acc.aggrTypes, aggrType] : acc.aggrTypes,
          calc: isCalc ? acc.calc + 1 : acc.calc,
        };
      }

      return acc;
    },
    Object.assign({}, EMPTY_SORT_COUNTS)
  );
};

/**
 * Reduce the calcsList to find either calc or prop dependencies
 * @param calcsList
 * @param matcherFn
 * @param isCalc
 * @param propName
 * @returns
 */
const reduceCalcDependencies = (
  calcsList: ICalcs[],
  matcherFn: (arg: TCalcArg) => boolean,
  isCalc: boolean,
  propName?: string
): CalcCounts => {
  const calcDependencies = calcsList.reduce(
    (acc, calc) => {
      const {
        prop: propCount,
        aggr: aggrCount,
        aggrTypes,
        calc: calcCount,
      } = reduceCalcArgs(calc.arguments, matcherFn);

      // if propCount > 0, append to the array
      const propDependency =
        propCount > 0
          ? [
              ...acc.prop,
              {
                name: calc.description,
                id: calc.id,
                aggrTypes: [],
                propName: isCalc ? calc.outputKeyName : propName,
              },
            ]
          : acc.prop;

      // if aggrCount > 0, append to the array
      const aggrDependency =
        aggrCount > 0
          ? [
              ...acc.aggr,
              {
                name: calc.description,
                id: calc.id,
                aggrTypes,
              },
            ]
          : acc.aggr;

      // if calcCount > 0, append to the array
      const calcDependency =
        calcCount > 0
          ? [
              ...acc.calc,
              {
                name: calc.description,
                id: calc.id,
                aggrTypes: [],
                propName: isCalc ? calc.outputKeyName : propName,
              },
            ]
          : acc.calc;

      return {
        ...acc,
        prop: propDependency,
        aggr: aggrDependency,
        calc: calcDependency,
      };
    },
    Object.assign({}, EMPTY_CALC_COUNTS)
  );

  return calcDependencies;
};

/**
 * Get counts of calcs referencing the prop or aggregate in the calcList
 * @param calcsList
 * @param props
 * @returns CalcCounts
 */
const getCalcDependenciesForProps = (calcsList: ICalcs[], props: IOutputItem[]): CalcCounts => {
  let result = props.reduce(
    (acc: CalcCounts, property) => {
      const { conceptId, uri: propUri } = property;

      const matcherFn = (arg: TCalcArg) => arg.concept === conceptId && arg.property === propUri;
      const calcDependencies = reduceCalcDependencies(
        calcsList,
        matcherFn,
        false,
        getPropertyName(propUri) || ''
      );

      return {
        ...acc,
        prop: [...acc.prop, ...calcDependencies.prop],
        aggr: [...acc.aggr, ...calcDependencies.aggr],
        calc: [...acc.calc, ...calcDependencies.calc],
      };
    },
    Object.assign({}, EMPTY_CALC_COUNTS)
  );

  return result;
};

/**
 * Get counts of calcs referencing the prop or aggregate in the calcList
 * @param calcsList
 * @param selectedCalcs - this is passed in as an array, but there is only 1 calculation
 * @returns CalcCounts
 */
const getCalcDependenciesForCalcs = (calcsList: ICalcs[], selectedCalcs: ICalcs[]): CalcCounts => {
  let result = selectedCalcs.reduce(
    (acc: CalcCounts, individualCalc) => {
      const matcherFn = (arg: TCalcArg) => arg.calcId === individualCalc.id;

      const calcDependencies = reduceCalcDependencies(calcsList, matcherFn, true);

      return {
        ...acc,
        prop: [...acc.prop, ...calcDependencies.prop],
        aggr: [...acc.aggr, ...calcDependencies.aggr],
        calc: [...acc.calc, ...calcDependencies.calc],
      };
    },
    Object.assign({}, EMPTY_CALC_COUNTS)
  );

  return result;
};

/**
 * Checks if the prop is used in the question filters
 * @param props
 * @param filterPropDeps
 * @returns boolean
 */
export const getIsPropUsedInQuestionWideFilter = (
  props: IOutputItem[],
  filterPropDeps: filterDepsType
): boolean => {
  const output = props.some((prop) => prop.uri in (filterPropDeps?.[prop.conceptId] || {}));
  return output;
};

/**
 * Checks if the calc is used in the question filters
 * @param calcs
 * @param filterPropDeps
 * @returns boolean
 */
export const getIsCalcUsedInQuestionWideFilter = (
  calcs: ICalcs[],
  filterPropDeps: filterDepsType
): boolean => {
  return calcs.some((calc) => {
    return calc.id in filterPropDeps;
  });
};

/**
 * Recursively checks filters and their children for dependencies on the target property URIs.
 * @param filters - The array of filter objects to search.
 * @param propsToCheckUris - An array of property URIs to check for dependencies against.
 * @returns {boolean} - True if a dependency is found, otherwise false.
 */
const checkFiltersRecursively = (
  filters: PropertyFilterItem[],
  propsToCheck: string[]
): boolean => {
  for (const filter of filters) {
    if (!filter) continue;

    // Check the current filter for a dependency
    if (
      filter.conditionType === 'property' &&
      filter.conditionTypeValue &&
      propsToCheck.includes(filter.conditionTypeValue)
    ) {
      return true;
    }

    // Search nested children recursively if any exist
    if (Array.isArray(filter.children) && filter.children.length > 0) {
      if (checkFiltersRecursively(filter.children, propsToCheck)) {
        return true;
      }
    }
  }

  return false;
};

export const getIsPropUsedInPropertyFilter = (
  props: IOutputItem[],
  definition: IQuestionDefinition
): IPropertyFilterDependency[] => {
  try {
    const propsToCheck = props.map((p) => `${p.conceptId}#${p.label}`);

    if (!definition || propsToCheck.length === 0) {
      return [];
    }

    return Object.entries(definition).reduce((dependencies, [conceptId, concept]) => {
      if (!concept?.properties || !Array.isArray(concept.properties)) {
        return dependencies;
      }

      const foundDependencies = concept.properties.reduce((propDependencies, property) => {
        if (
          property?.filters &&
          Array.isArray(property.filters) &&
          checkFiltersRecursively(property.filters, propsToCheck)
        ) {
          propDependencies.push({
            conceptId: conceptId,
            conceptLabel: concept.label,
            propertyUri: property.uri,
            propertyLabel: property.label,
            prop: {
              ...property,
              color: concept.color!,
              isExisting: true,
              conceptUri: concept.uri,
              conceptName: concept.label,
              conceptId: conceptId,
            } as IOutputItem,
          });
        }
        return propDependencies;
      }, [] as IPropertyFilterDependency[]);
      return dependencies.concat(foundDependencies);
    }, [] as IPropertyFilterDependency[]);
  } catch (error) {
    console.error('Error building filter dependency map:', error);
    return [];
  }
};

/**
 * returns the various runtime sorting & calc deps for a given property
 * @param prop
 * @returns RuntimeDependencies
 */
export default function getRuntimeSettingsRefs(
  props: (IOutputItem | ICalcs)[],
  isCalc: boolean,
  calcsList: ICalcs[],
  sortSettings: IRuntimeSort[],
  filterPropDeps: filterDepsType,
  definition: IQuestionDefinition,
  allPropertyWindowFunctions: TPropertyDepWindowFunctions[],
  allCalcWindowFunctions: ICalcs[]
): IRuntimeDependencies {
  const sortCounts = isCalc
    ? getSortCountsForCalcs(sortSettings, props as ICalcs[])
    : getSortCountsForProps(sortSettings, props as IOutputItem[]);

  const calcCounts = isCalc
    ? getCalcDependenciesForCalcs(calcsList, props as ICalcs[])
    : getCalcDependenciesForProps(calcsList, props as IOutputItem[]);

  const isSorted =
    sortCounts.prop > 0 ||
    sortCounts.aggr > 0 ||
    sortCounts.aggrTypes.length > 0 ||
    sortCounts.calc > 0;

  const hasCalcs =
    calcCounts.prop.length > 0 || calcCounts.aggr.length > 0 || calcCounts.calc.length > 0;

  const isUsedInQuestionWideFilter = isCalc
    ? getIsCalcUsedInQuestionWideFilter(props as ICalcs[], filterPropDeps)
    : getIsPropUsedInQuestionWideFilter(props as IOutputItem[], filterPropDeps);

  const usedInPropertyFilters =
    isCalc || definition === undefined
      ? []
      : getIsPropUsedInPropertyFilter(props as IOutputItem[], definition);

  const WFDeps = getWFDeps(
    props as IOutputItem[],
    allPropertyWindowFunctions,
    allCalcWindowFunctions,
    isCalc
  );

  return {
    sortCounts,
    calcCounts,
    isSorted,
    hasCalcs,
    isUsedInQuestionWideFilter,
    usedInPropertyFilters,
    WFDeps,
  } as IRuntimeDependencies;
}
